import {
    agentRunLedgerService,
    hasCloudAuditRunPaginationMetadata,
    type AgentRunLedgerService,
    type CloudAuditListOptions,
} from '@common/api/agentRunLedgerService';
import {
    pluginAuditLogService,
    type PluginAuditListOptions,
    type PluginAuditLogService,
    type PluginAuditTaskSummaryResponse,
} from '@common/api/pluginAuditLogService';
import {
    BUSTLY_CORE_PRODUCT_CODE,
    PLUGIN_PRODUCT_CODE,
    type ProductCode,
} from '@common/api/productContext';
import {
    adaptCloudAgentRunAuditContent,
    adaptCloudAgentRunAuditConversation,
    adaptCloudAgentRunAuditEventListToRunList,
    adaptCloudAgentRunAuditRunList,
    type CloudAgentRunAuditContentView,
    type CloudAgentRunAuditConversationView,
    type CloudAgentRunAuditListView,
} from '@/view-models/cloud-agent-run-audit';

const LEGACY_AUDIT_RUN_LIMIT = 200;
const FALLBACK_AUDIT_EVENT_LIMIT = 100;

export type AuditPagination =
    | { kind: 'cursor'; nextCursor: string | null }
    | { kind: 'local' };

export type AuditLogListQuery = PluginAuditListOptions;

export interface AuditLogListResult {
    data: CloudAgentRunAuditListView;
    pagination: AuditPagination;
}

export type AuditLogDetail =
    | { kind: 'conversation'; conversation: CloudAgentRunAuditConversationView }
    | { kind: 'task-summary'; summary: PluginAuditTaskSummaryResponse };

export interface AuditLogDataSource {
    readonly detailKind: 'conversation' | 'task-summary';
    readonly productCode: ProductCode;
    getDetail(workspaceId: string, runId: string): Promise<AuditLogDetail>;
    getEventContent?(workspaceId: string, eventId: string): Promise<CloudAgentRunAuditContentView>;
    listRuns(workspaceId: string, query: AuditLogListQuery): Promise<AuditLogListResult>;
}

const getHttpStatus = (error: unknown): number | null => {
    if (!error || typeof error !== 'object') return null;
    const response = (error as { response?: unknown }).response;
    if (!response || typeof response !== 'object') return null;
    const status = (response as { status?: unknown }).status;
    return typeof status === 'number' ? status : null;
};

const getApiErrorCode = (error: unknown): string | null => {
    if (!error || typeof error !== 'object') return null;
    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string') return code;
    const response = (error as { response?: unknown }).response;
    if (!response || typeof response !== 'object') return null;
    const responseCode = (response as { code?: unknown }).code;
    return typeof responseCode === 'string' ? responseCode : null;
};

const shouldFallbackToDesktopEventList = (error: unknown): boolean => {
    const status = getHttpStatus(error);
    if (status === 404 || status === 405) return true;
    const code = getApiErrorCode(error);
    if (code === 'ECONNABORTED') return true;
    return code === '404' || code === '405' || code === 'NOT_FOUND';
};

export const createDesktopAuditLogDataSource = (
    service: AgentRunLedgerService = agentRunLedgerService,
): AuditLogDataSource => ({
    detailKind: 'conversation',
    productCode: BUSTLY_CORE_PRODUCT_CODE,
    async listRuns(workspaceId, query) {
        const desktopOptions: CloudAuditListOptions = {
            limit: query.limit,
            ...(query.cursor ? { cursor: query.cursor } : {}),
            ...(query.startTime ? { startTime: query.startTime } : {}),
            ...(query.endTime ? { endTime: query.endTime } : {}),
        };
        try {
            const response = await service.listRuns(workspaceId, desktopOptions);
            if (!hasCloudAuditRunPaginationMetadata(response) && !query.cursor) {
                const legacyResponse = await service.listRuns(workspaceId, {
                    limit: LEGACY_AUDIT_RUN_LIMIT,
                    ...(query.startTime ? { startTime: query.startTime } : {}),
                    ...(query.endTime ? { endTime: query.endTime } : {}),
                });
                return {
                    data: adaptCloudAgentRunAuditRunList(legacyResponse),
                    pagination: { kind: 'local' },
                };
            }
            return {
                data: adaptCloudAgentRunAuditRunList(response),
                pagination: {
                    kind: 'cursor',
                    nextCursor: response.hasMore === false ? null : response.nextCursor || null,
                },
            };
        } catch (error: unknown) {
            if (!shouldFallbackToDesktopEventList(error)) throw error;
            const response = await service.listEvents(workspaceId, {
                limit: FALLBACK_AUDIT_EVENT_LIMIT,
                ...(query.startTime ? { startTime: query.startTime } : {}),
                ...(query.endTime ? { endTime: query.endTime } : {}),
            });
            return {
                data: adaptCloudAgentRunAuditEventListToRunList(response),
                pagination: { kind: 'local' },
            };
        }
    },
    async getDetail(workspaceId, runId) {
        const response = await service.getRunConversation(workspaceId, runId);
        return { kind: 'conversation', conversation: adaptCloudAgentRunAuditConversation(response) };
    },
    async getEventContent(workspaceId, eventId) {
        return adaptCloudAgentRunAuditContent(await service.getEventContent(workspaceId, eventId));
    },
});

export const createPluginAuditLogDataSource = (
    service: PluginAuditLogService = pluginAuditLogService,
): AuditLogDataSource => ({
    detailKind: 'task-summary',
    productCode: PLUGIN_PRODUCT_CODE,
    async listRuns(workspaceId, query) {
        const response = await service.listRuns(workspaceId, query);
        const compatibleResponse = {
            ...response,
            runs: response.runs.map((run) => ({
                ...run,
                conversationContentState: run.summaryContentState,
            })),
        };
        return {
            data: adaptCloudAgentRunAuditRunList(compatibleResponse),
            pagination: {
                kind: 'cursor',
                nextCursor: response.hasMore ? response.nextCursor || null : null,
            },
        };
    },
    async getDetail(workspaceId, runId) {
        return { kind: 'task-summary', summary: await service.getTaskSummary(workspaceId, runId) };
    },
});

const DATA_SOURCES: Record<ProductCode, AuditLogDataSource> = {
    [BUSTLY_CORE_PRODUCT_CODE]: createDesktopAuditLogDataSource(),
    [PLUGIN_PRODUCT_CODE]: createPluginAuditLogDataSource(),
};

export const getAuditLogDataSource = (productCode: ProductCode): AuditLogDataSource => (
    DATA_SOURCES[productCode]
);
